import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'

// The account holder's enrollments across all their participants.
// Unpaid pay_course rows get a "Pay now" button (re-opens Stripe Checkout);
// active rows show the course WhatsApp link when the course has one.

export function MyEnrollmentsPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  // Prepay offer for the current month, if there is one to take. Best effort:
  // a failure here must never break the enrollments list.
  const [offer, setOffer] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    api.enrollments.list()
      .then((d) => setRows(d.enrollments || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user) return
    reload()
    api.prepay.offer()
      .then((d) => setOffer(d?.enabled && (d.lines || []).length > 0 ? d : null))
      .catch(() => setOffer(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user])

  async function payNow(row) {
    setBusyId(row.id); setError(null)
    try {
      const d = await api.payments.checkout(row.payment_id)
      if (d.checkout_url) window.location.assign(d.checkout_url)
    } catch (e) {
      setError(e.code || 'server')
      setBusyId(null)
    }
  }

  async function cancel(row) {
    if (!confirm(t('enrollments.confirm_cancel', { name: row.participant_name, course: row.course_name }))) return
    setBusyId(row.id); setError(null)
    try {
      await api.enrollments.cancel(row.id)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user) {
    return <main className="container"><p className="error">{t('auth.errors.not_authenticated')}</p></main>
  }

  return (
    <main className="container">
      <h1>{t('enrollments.title')}</h1>
      <p className="muted">{t('enrollments.intro')}</p>

      {offer && (
        <p className="card notice">
          {t('prepay.banner', {
            percent: offer.percent,
            saving: fmtPrice(offer.totals?.saving ?? 0),
          })}{' '}
          <Link to={`/prepay/${offer.month}`}>{t('prepay.banner_cta')}</Link>
        </p>
      )}

      {error && (
        <p className="error">
          {t(`enrollments.errors.${error}`, { defaultValue: t(`auth.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}
      {loading && <p>{t('common.loading')}</p>}

      {!loading && rows.length === 0 && (
        <p className="muted">
          {t('enrollments.empty_state')} <Link to="/courses">{t('enrollments.browse_courses')}</Link>
        </p>
      )}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('enrollments.col_participant')}</th>
              <th>{t('enrollments.col_course')}</th>
              <th>{t('enrollments.col_status')}</th>
              <th>{t('enrollments.col_payment')}</th>
              <th>{t('enrollments.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.participant_name}</td>
                <td>
                  <Link to={`/courses/${r.course_slug}`}>{r.course_name}</Link>
                  {r.whatsapp_link && (
                    <>
                      {' · '}
                      <a href={r.whatsapp_link} target="_blank" rel="noreferrer">
                        {t('enrollments.whatsapp')}
                      </a>
                    </>
                  )}
                </td>
                <td>{t(`enrollments.status_${r.status}`, { defaultValue: r.status })}</td>
                <td>
                  {r.payment_id
                    ? <>{t(`enrollments.payment_${r.payment_status}`, { defaultValue: r.payment_status })} · {fmtPrice(r.payment_amount)}</>
                    : <span className="muted">—</span>}
                </td>
                <td>
                  {r.status === 'pending_payment' && r.payment_status === 'pending' && (
                    <button type="button" disabled={busyId === r.id} onClick={() => payNow(r)}>
                      {t('enrollments.pay_now')}
                    </button>
                  )}
                  {r.status !== 'cancelled' && (
                    <button
                      type="button"
                      className="link-button danger"
                      style={{ marginLeft: '0.5rem' }}
                      disabled={busyId === r.id}
                      onClick={() => cancel(r)}
                    >
                      {t('enrollments.cancel')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
